/**
 * useDevice — read-only device-class flags derived from navigator + viewport.
 *
 * `isMobileOrTablet` is consulted by render-side decisions (compass button
 * only on touch devices, 3D-terrain toggle hidden on touch), and by the
 * install-prompt heuristic. `isIOS` distinguishes the iOS Safari "Add to
 * Home Screen" path from the Chrome beforeinstallprompt path. `isStandalone`
 * suppresses the install prompt when the app is already installed.
 *
 * Memoised so the values are referentially stable across renders.
 *
 * Consumers: App.tsx (render gates), useInstallPrompt (eligibility checks).
 */
import { useMemo } from "react";

export interface DeviceFlags {
  isMobileOrTablet: boolean;
  isIOS: boolean;
  isStandalone: boolean;
}

export function useDevice(): DeviceFlags {
  const isMobileOrTablet = useMemo(
    () => /Android|iPad|iPhone|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
      || (navigator.maxTouchPoints > 0 && window.innerWidth <= 1024),
    [],
  );
  const isIOS = useMemo(() => /iPad|iPhone|iPod/.test(navigator.userAgent), []);
  // iOS Safari exposes `navigator.standalone`; not in lib.dom.d.ts but reliably
  // present on home-screen launches.
  const isStandalone = useMemo(
    () => window.matchMedia("(display-mode: standalone)").matches
      || (navigator as Navigator & { standalone?: boolean }).standalone === true,
    [],
  );

  return { isMobileOrTablet, isIOS, isStandalone };
}
